import React, { useCallback, useEffect, useState } from 'react'
import { lockBodyScroll } from '@/utils/bodyScrollLock'
import useAuth from '@/hooks/useAuth'
import paymentMethodService, { type PrimaryPaymentMethod } from '@/services/paymentMethodService'
import '@/styles/components/change-items-modal.css'

const PAYPAL_URL = 'https://www.paypal.com/cgi-bin/webscr'
const PAYPAL_BUSINESS = 'paypal@mcqueensdairies.co.uk'

function PayOnline(): React.JSX.Element {
  const { user } = useAuth()
  const [amount, setAmount] = useState('')
  const [managedPageLoading, setManagedPageLoading] = useState(false)
  const [managedPageError, setManagedPageError] = useState<string | null>(null)
  const [paymentMethod, setPaymentMethod] = useState<PrimaryPaymentMethod | null>(null)
  // 'error' means we couldn't determine the payment method (request failed or
  // returned nothing, e.g. ChargeBee not configured on this environment). In
  // that case we still offer PayPal so PayPal-only customers aren't stranded.
  const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>('loading')
  // When status is 'error', why: 'not_linked' = the account has no payment
  // account set up in our billing system (a 422, retrying will not help);
  // 'unavailable' = the lookup itself failed and may be transient (try again).
  const [errorReason, setErrorReason] = useState<'not_linked' | 'unavailable'>('unavailable')
  const [paypalModalOpen, setPaypalModalOpen] = useState(false)

  const fetchPaymentMethod = useCallback(async () => {
    try {
      const result = await paymentMethodService.getPrimary()
      setPaymentMethod(result)
      if (result) {
        setStatus('loaded')
      } else {
        setErrorReason('unavailable')
        setStatus('error')
      }
    } catch (err) {
      const httpStatus = (err as { status?: number })?.status
      setPaymentMethod(null)
      setErrorReason(httpStatus === 422 ? 'not_linked' : 'unavailable')
      setStatus('error')
    }
  }, [])

  useEffect(() => {
    fetchPaymentMethod()
  }, [fetchPaymentMethod])

  // Refresh on tab focus so returning from the ChargeBee hosted page reflects
  // a newly-added payment method without a manual reload.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') fetchPaymentMethod()
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [fetchPaymentMethod])

  useEffect(() => {
    if (!paypalModalOpen) return

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPaypalModalOpen(false)
    }
    document.addEventListener('keydown', handleEscape)
    const releaseScroll = lockBodyScroll()

    return () => {
      document.removeEventListener('keydown', handleEscape)
      releaseScroll()
    }
  }, [paypalModalOpen])

  const isValid = (() => {
    const num = parseFloat(amount)
    return !isNaN(num) && num > 0
  })()

  async function handleManagePaymentMethods() {
    setManagedPageLoading(true)
    setManagedPageError(null)
    try {
      const url = await paymentMethodService.getManagedPageUrl()
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch {
      setManagedPageError('Unable to open payment management page. Please try again.')
    } finally {
      setManagedPageLoading(false)
    }
  }

  const hasPaymentMethod = status === 'loaded' && paymentMethod?.has_payment_method === true
  // Set up but the bank hasn't confirmed the Direct Debit mandate yet.
  const paymentPending = hasPaymentMethod && paymentMethod?.pending === true
  const noPaymentMethod = status === 'loaded' && paymentMethod !== null && !paymentMethod.has_payment_method
  // Couldn't determine the saved method, fall back to PayPal so the customer
  // can always pay.
  const methodUnavailable = status === 'error'

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) setPaypalModalOpen(false)
  }

  return (
    <div className="pay-online">
      <div className="pay-online-panel">
        <div className="pay-online-header">
          <h2>Payments</h2>
        </div>

        <div className="pay-online-sections">
          {status === 'loading' && (
            <div className="pay-block">
              <p className="pay-block-body">Loading your payment options…</p>
            </div>
          )}

          {methodUnavailable && errorReason === 'not_linked' && (
            <div className="pay-block">
              <h3 className="pay-block-title">No payment account set up</h3>
              <p className="pay-block-body">
                Your account isn&apos;t linked to our online payment system yet, so there&apos;s no saved payment method to show or set up here. You can still pay a balance with PayPal below.
              </p>
            </div>
          )}

          {methodUnavailable && errorReason === 'unavailable' && (
            <div className="pay-block">
              <h3 className="pay-block-title">We couldn&apos;t load your payment details</h3>
              <p className="pay-block-body">
                We&apos;re having trouble reaching our payment provider right now. This is usually temporary, please use "Try again" in a moment. You can also pay a balance with PayPal below.
              </p>
            </div>
          )}

          {hasPaymentMethod && (
            <div className="pay-block">
              <h3 className="pay-block-title">
                {paymentPending
                  ? 'Payment method set up (pending confirmation)'
                  : 'Current Payment Method'}
              </h3>
              {paymentPending ? (
                <p className="pay-block-body">
                  {paymentMethod?.label} is set up and is waiting for your bank
                  to confirm the mandate. This usually takes a few working days,
                  and there&apos;s nothing more you need to do.
                </p>
              ) : (
                <p className="pay-block-body">
                  Your current primary payment method is {paymentMethod?.label}
                </p>
              )}
              <p className="pay-block-body">
                If you need to, you can update your payment method below.
              </p>
              <p className="pay-block-meta">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Securely managed by our payment partner.
              </p>
            </div>
          )}

          {noPaymentMethod && (
            <div className="pay-block">
              <h3 className="pay-block-title">No payment method set up</h3>
              <p className="pay-block-body">
                We don&apos;t currently have a payment method linked to your account. To keep your deliveries running smoothly, please set up a Direct Debit today.
              </p>
            </div>
          )}

          {managedPageError && (
            <p className="pay-online-error">{managedPageError}</p>
          )}

          <div className="pay-actions">
            {hasPaymentMethod && (
              <button
                type="button"
                className="btn-pay-action"
                onClick={handleManagePaymentMethods}
                disabled={managedPageLoading}
              >
                {managedPageLoading ? 'Loading…' : 'Update Payment Method'}
              </button>
            )}

            {noPaymentMethod && (
              <>
                <button
                  type="button"
                  className="btn-pay-action"
                  onClick={handleManagePaymentMethods}
                  disabled={managedPageLoading}
                >
                  {managedPageLoading ? 'Loading…' : 'Set Up Payment Method'}
                </button>

                <button
                  type="button"
                  className="btn-pay-action"
                  onClick={() => setPaypalModalOpen(true)}
                >
                  Pay with PayPal
                </button>
              </>
            )}

            {methodUnavailable && (
              <>
                <button
                  type="button"
                  className="btn-pay-action"
                  onClick={() => setPaypalModalOpen(true)}
                >
                  Pay with PayPal
                </button>

                {errorReason === 'unavailable' && (
                  <button
                    type="button"
                    className="btn-pay-action"
                    onClick={fetchPaymentMethod}
                  >
                    Try again
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {paypalModalOpen && (
        <div className="modal-backdrop" onClick={handleBackdropClick}>
          <div className="modal-container" role="dialog" aria-modal="true">
            <div className="modal-header">
              <h2>Pay with PayPal</h2>
              <button
                className="modal-close-btn"
                onClick={() => setPaypalModalOpen(false)}
                aria-label="Close modal"
              >
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="modal-content">
              <p style={{ marginBottom: '1.5rem' }}>
                McQueens offer the ability to pay for your milk deliveries online through our own website.
                Fill in your details below and you will be redirected to the PayPal website to complete the payment.
              </p>
              <p style={{ marginBottom: '1.5rem' }}>
                If you have any troubles please contact us directly and we will be happy to help!
              </p>

              <form action={PAYPAL_URL} method="post" target="_blank">
                <input type="hidden" name="cmd" value="_xclick" />
                <input type="hidden" name="business" value={PAYPAL_BUSINESS} />
                <input type="hidden" name="item_name" value="McQueens Dairies Payment" />
                <input type="hidden" name="amount" value={amount} />
                <input type="hidden" name="currency_code" value="GBP" />
                <input type="hidden" name="no_shipping" value="1" />
                {user?.tsg_chargebeeid && (
                  <input type="hidden" name="custom" value={user.tsg_chargebeeid} />
                )}

                <div className="form-group">
                  <label htmlFor="payAmount" className="form-label">Total Amount Payable (inc VAT):</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '1.1rem', fontWeight: 600 }}>&pound;</span>
                    <input
                      id="payAmount"
                      type="number"
                      className="form-control"
                      placeholder="0.00"
                      min="0.01"
                      step="0.01"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      style={{ flex: 1 }}
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" disabled={!isValid}>
                    Pay with PayPal
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PayOnline
